import {
    Column,
    Container,
    cssVar,
    Divider,
    Grid,
    Header,
    Label,
    Row,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const TableContainer = styled(Container).withConfig({
    componentId: 'TableContainer',
})`
    border: 1px solid ${cssVar.colorStrokeLight};
    border-radius: 4px;
    position: relative;
    overflow: hidden; /* Important to contain the shadows */

    ${({ $mobile, $tablet, $showLeftShadow, $showRightShadow }) =>
        ($mobile || $tablet) &&
        `
        width: 90vw;

        /* Move shadows to the container */
        &:before, &:after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            width: 15px;
            z-index: 2;
            pointer-events: none;
            transition: opacity 0.3s ease;
        }

        &:before {
            left: 0;
            background: linear-gradient(to right, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0) 100%);
            opacity: ${$showLeftShadow ? 1 : 0};
        }

        &:after {
            right: 0;
            background: linear-gradient(to left, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0) 100%);
            opacity: ${$showRightShadow ? 1 : 0};
        }
    `}
`;

const TableWrapper = styled.div`
    width: 100%;
    position: relative;

    ${({ $mobile, $tablet }) =>
        ($mobile || $tablet) &&
        `
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    `}
`;

const TableContent = styled.div.withConfig({
    componentId: 'TableContent',
})`
    min-width: 960px; /* Force minimum width */
    display: table; /* Use table display model */
    width: 100%;
`;

const HeaderCell = styled(Container)`
    background: ${cssVar.colorStrokeVeryLight};
    padding: 10px;
`;

const BodyCell = styled(Container).withConfig({
    componentId: 'BodyCell',
})`
    padding: 10px;
    ${({ $top }) => $top && `border-top: 1px solid ${cssVar.colorStrokeVeryLight}`}
    ${({ $bottom }) => $bottom && `border-bottom: 1px solid ${cssVar.colorStrokeVeryLight}`}
    ${({ $right }) => $right && `border-right: 1px solid ${cssVar.colorStrokeVeryLight}`}
    ${({ $left }) => $left && `border-left: 1px solid ${cssVar.colorStrokeVeryLight}`}
`;

const ProfileTerms = (props) => {
    const alignments = useAlignments();
    const sizes = useSizes();
    const { isMobile: mobile, isTablet: tablet } = useScreen();
    const terms = props.policyTerms || [];
    const tableRefs = React.useRef([]);
    const [scrollStates, setScrollStates] = React.useState([]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only fetch
    React.useEffect(() => {
        props.doGetPolicyTerms();
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on listed deps
    React.useEffect(() => {
        // Initialize scroll states for each table
        if (terms.length > 0) {
            setScrollStates(
                terms.map(() => ({
                    showLeftShadow: false,
                    showRightShadow: true,
                })),
            );

            // Initialize refs array
            tableRefs.current = tableRefs.current.slice(0, terms.length);
        }
    }, [terms.length]);

    const handleScroll = (index) => {
        const tableWrapper = tableRefs.current[index];
        if (!tableWrapper) return;

        const isAtStart = tableWrapper.scrollLeft <= 5;
        const isAtEnd = tableWrapper.scrollWidth - tableWrapper.clientWidth - tableWrapper.scrollLeft <= 5;

        setScrollStates((prev) => {
            const newStates = [...prev];
            newStates[index] = {
                showLeftShadow: !isAtStart,
                showRightShadow: !isAtEnd,
            };
            return newStates;
        });
    };

    return (
        <Container horizontalAlignment={alignments.left}>
            <Divider top={sizes.xl} />
            <Header
                size={sizes.lg}
                textAlign={alignments.left}
                text={<FormattedMessage id='Profile_Terms.terms' />}
                bottom={sizes.lg}
            />
            {terms.length > 0 &&
                terms.map((term, index) => (
                    <TableContainer
                        // biome-ignore lint/suspicious/noArrayIndexKey: terms are ordered chronologically and stable
                        key={index}
                        bottom={sizes.lg}
                        $mobile={mobile}
                        $tablet={tablet}
                        $showLeftShadow={scrollStates[index]?.showLeftShadow}
                        $showRightShadow={scrollStates[index]?.showRightShadow}
                    >
                        <Grid>
                            <Row nogutter>
                                {mobile || tablet ? (
                                    <Column xs={12} nogutter>
                                        <HeaderCell fullSize>
                                            <Label
                                                bold
                                                textAlign={alignments.left}
                                                text={
                                                    <FormattedMessage
                                                        id='Profile_Terms.term'
                                                        values={{ term: term.term }}
                                                    />
                                                }
                                                size={sizes.lg}
                                            />
                                            <Label
                                                bold
                                                textAlign={alignments.left}
                                                text={
                                                    <FormattedMessage
                                                        id='Profile_Terms.policyNumber'
                                                        values={{ number: term.policyNumber }}
                                                    />
                                                }
                                                top={sizes.xs}
                                            />
                                        </HeaderCell>
                                    </Column>
                                ) : (
                                    <>
                                        <Column xs={6} nogutter>
                                            <HeaderCell fullSize>
                                                <Label
                                                    bold
                                                    textAlign={alignments.left}
                                                    text={
                                                        <FormattedMessage
                                                            id='Profile_Terms.term'
                                                            values={{ term: term.term }}
                                                        />
                                                    }
                                                    size={sizes.lg}
                                                />
                                            </HeaderCell>
                                        </Column>
                                        <Column xs={6} nogutter>
                                            <HeaderCell fullSize>
                                                <Label
                                                    bold
                                                    textAlign={alignments.right}
                                                    text={
                                                        <FormattedMessage
                                                            id='Profile_Terms.policyNumber'
                                                            values={{ number: term.policyNumber }}
                                                        />
                                                    }
                                                />
                                            </HeaderCell>
                                        </Column>
                                    </>
                                )}
                            </Row>
                            <TableWrapper
                                $mobile={mobile}
                                $tablet={tablet}
                                ref={(el) => (tableRefs.current[index] = el)}
                                onScroll={() => handleScroll(index)}
                            >
                                <TableContent>
                                    <Row nogutter>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell $bottom>
                                                <Label
                                                    bold
                                                    textAlign={alignments.left}
                                                    text={<FormattedMessage id='Profile_Terms.transactionType' />}
                                                />
                                            </BodyCell>
                                        </Column>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell $bottom>
                                                <Label
                                                    bold
                                                    textAlign={alignments.left}
                                                    text={<FormattedMessage id='Profile_Terms.effectiveDate' />}
                                                />
                                            </BodyCell>
                                        </Column>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell $bottom>
                                                <Label
                                                    bold
                                                    textAlign={alignments.left}
                                                    text={<FormattedMessage id='Profile_Terms.expiryDate' />}
                                                />
                                            </BodyCell>
                                        </Column>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell $bottom>
                                                <Label
                                                    bold
                                                    textAlign={alignments.right}
                                                    text={<FormattedMessage id='Profile_Terms.premium' />}
                                                />
                                            </BodyCell>
                                        </Column>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell $bottom>
                                                <Label
                                                    bold
                                                    textAlign={alignments.right}
                                                    text={<FormattedMessage id='Profile_Terms.taxes' />}
                                                />
                                            </BodyCell>
                                        </Column>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell $bottom>
                                                <Label
                                                    bold
                                                    textAlign={alignments.right}
                                                    text={<FormattedMessage id='Profile_Terms.fees' />}
                                                />
                                            </BodyCell>
                                        </Column>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell $bottom>
                                                <Label
                                                    bold
                                                    textAlign={alignments.right}
                                                    text={<FormattedMessage id='Profile_Terms.total' />}
                                                />
                                            </BodyCell>
                                        </Column>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell $bottom>
                                                <Label
                                                    bold
                                                    textAlign={alignments.left}
                                                    text={<FormattedMessage id='Profile_Terms.status' />}
                                                />
                                            </BodyCell>
                                        </Column>
                                    </Row>
                                    {term.transactions.map((transaction, index) => (
                                        // biome-ignore lint/suspicious/noArrayIndexKey: transactions are ordered chronologically and stable
                                        <Row nogutter key={index}>
                                            <Column xs={12 / 8} nogutter>
                                                <BodyCell $bottom={index === term.transactions.length - 1}>
                                                    {transaction.guid ? (
                                                        <Label
                                                            clickable
                                                            underline
                                                            color={cssVar.colorSecondary}
                                                            textAlign={alignments.left}
                                                            text={transaction.type}
                                                            onClick={() => {
                                                                window.location.href = `${props.context.configuration.policyWeb}/profile?entity=${transaction.guid}`;
                                                            }}
                                                        />
                                                    ) : (
                                                        <Label textAlign={alignments.left} text={transaction.type} />
                                                    )}
                                                </BodyCell>
                                            </Column>
                                            <Column xs={12 / 8} nogutter>
                                                <BodyCell $bottom={index === term.transactions.length - 1}>
                                                    <Label
                                                        textAlign={alignments.left}
                                                        text={transaction.effectiveDate}
                                                    />
                                                </BodyCell>
                                            </Column>
                                            <Column xs={12 / 8} nogutter>
                                                <BodyCell $bottom={index === term.transactions.length - 1}>
                                                    <Label textAlign={alignments.left} text={transaction.expiryDate} />
                                                </BodyCell>
                                            </Column>
                                            <Column xs={12 / 8} nogutter>
                                                <BodyCell $bottom={index === term.transactions.length - 1}>
                                                    <Label textAlign={alignments.right} text={transaction.premium} />
                                                </BodyCell>
                                            </Column>
                                            <Column xs={12 / 8} nogutter>
                                                <BodyCell $bottom={index === term.transactions.length - 1}>
                                                    <Label textAlign={alignments.right} text={transaction.taxes} />
                                                </BodyCell>
                                            </Column>
                                            <Column xs={12 / 8} nogutter>
                                                <BodyCell $bottom={index === term.transactions.length - 1}>
                                                    <Label textAlign={alignments.right} text={transaction.fees} />
                                                </BodyCell>
                                            </Column>
                                            <Column xs={12 / 8} nogutter>
                                                <BodyCell $bottom={index === term.transactions.length - 1}>
                                                    <Label textAlign={alignments.right} text={transaction.total} />
                                                </BodyCell>
                                            </Column>
                                            <Column xs={12 / 8} nogutter>
                                                <BodyCell $bottom={index === term.transactions.length - 1}>
                                                    <Label textAlign={alignments.left} text={transaction.status} />
                                                </BodyCell>
                                            </Column>
                                        </Row>
                                    ))}
                                    <Row nogutter>
                                        <Column xs={12 / 4} nogutter />
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell>
                                                <Label
                                                    textAlign={alignments.right}
                                                    text={<FormattedMessage id='Profile_Terms.totalLabel' />}
                                                />
                                            </BodyCell>
                                        </Column>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell>
                                                <Label bold textAlign={alignments.right} text={term.totalPremium} />
                                            </BodyCell>
                                        </Column>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell>
                                                <Label bold textAlign={alignments.right} text={term.totalTaxes} />
                                            </BodyCell>
                                        </Column>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell>
                                                <Label bold textAlign={alignments.right} text={term.totalFees} />
                                            </BodyCell>
                                        </Column>
                                        <Column xs={12 / 8} nogutter>
                                            <BodyCell>
                                                <Label
                                                    bold
                                                    textAlign={alignments.right}
                                                    text={term.total}
                                                    size={sizes.md}
                                                />
                                            </BodyCell>
                                        </Column>
                                    </Row>
                                </TableContent>
                            </TableWrapper>
                        </Grid>
                    </TableContainer>
                ))}
        </Container>
    );
};

export default ProfileTerms;
